import {
  DateInputControl,
  GreyGrey80,
  Primary13Regular,
  SimpleModal,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  add,
  apiDatesAdapters,
  format,
  isBefore,
  startOfDay
} from '@sequencehq/utils/dates'
import { useState } from 'react'

type Props = {
  entityName: 'invoice' | 'credit note'
  entityDate: string
  lockDate: string
  onClose: () => void
  onSubmit: (newLockDate: string) => Promise<void>
}

const dateFromApi = (apiDate: string) =>
  apiDatesAdapters.fromApi(apiDate) ?? new Date()

/**
 * Remediation step: move the accounting lock date to on/before the entity date
 * so it can be sent. Shown only to users who can edit account settings; others
 * see the read-only lock-date ConfirmationBlocker.
 */
export const ControlledLockDateModal = ({
  entityName,
  entityDate,
  lockDate,
  onClose,
  onSubmit
}: Props) => {
  const entityDateValue = dateFromApi(entityDate)
  const [selectedDate, setSelectedDate] = useState<Date>(entityDateValue)
  const [isSaving, setIsSaving] = useState(false)

  // Floor the picker 10 years back, but never above the entity date, or an
  // entity dated >10 years ago would give maxDate < minDate (unusable range).
  const tenYearsAgo = add(startOfDay(new Date()), { years: -10 })
  const minDate = isBefore(entityDateValue, tenYearsAgo)
    ? entityDateValue
    : tenYearsAgo

  const handleSubmit = async () => {
    setIsSaving(true)

    try {
      await onSubmit(apiDatesAdapters.toApi(selectedDate))
    } catch {
      setIsSaving(false)
    }

    // Stay mounted either way: on success the stepper advances to the confirm
    // step; on failure the mutation surfaces its own error notification.
    return false
  }

  return (
    <SimpleModal
      isOpen
      variant="dialog"
      data-testid="lockDate"
      title="Update accounting lock date"
      submitButtonText="Update lock date"
      submittingButtonText="Updating..."
      submitting={isSaving}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <Flex flexDirection="column" gap="16px">
        <Box {...Primary13Regular} color={GreyGrey80}>
          The {entityName} date is before the lock date (
          {format(dateFromApi(lockDate), 'd MMM yyyy')}). Move the accounting
          lock date to {format(entityDateValue, 'd MMM yyyy')} or earlier to
          continue. This changes the lock date for the whole account.
        </Box>
        <DateInputControl
          inputProps={{
            id: 'accounting-lock-date',
            'data-testid': 'lockDate.date',
            width: '100%',
            isDisabled: isSaving
          }}
          onChange={setSelectedDate}
          value={selectedDate}
          minDate={minDate}
          maxDate={entityDateValue}
          popoverTestId="lockDate.calendar"
        />
      </Flex>
    </SimpleModal>
  )
}
